import React, { useState, useRef, useEffect } from "react";
import { ExpandMore as ChevronDown, ChevronRight, Add as Plus, Check, Close as X, Edit as Pencil, MoreVert, DataObject, ViewList } from "@mui/icons-material";
import JsonNode from "./JsonNode";
import { 
  Box, Typography, IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, 
  Button, Card, CardHeader, CardContent, CardActions, Chip, Menu, 
  alpha, useTheme, Autocomplete
} from "@mui/material";

export const ContainerCard = ({
  id,
  title,
  isCollapsed,
  data,
  isSchemaTab,
  onToggleCollapse,
  onRenameContainer,
  onUpdateContainerData,
  onAddField
}) => {
  const theme = useTheme();
  const [isAdding, setIsAdding] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("String");
  const [fieldValue, setFieldValue] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const fieldInputRef = useRef(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const titleInputRef = useRef(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    if (isAdding && fieldInputRef.current) {
      fieldInputRef.current.focus();
    }
  }, [isAdding]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const [isJsonView, setIsJsonView] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const handleToggleJsonView = () => {
    if (!isJsonView) {
      setJsonText(JSON.stringify(data, null, 2));
      setJsonError("");
      setIsJsonView(true);
    } else {
      try {
        const parsed = JSON.parse(jsonText);
        onUpdateContainerData(id, parsed);
        setIsJsonView(false);
      } catch (err) {
        setJsonError("Invalid JSON: " + err.message);
      }
    }
  };


  const handleStartAdd = (e) => {
    e.stopPropagation();
    setIsAdding(true);
    setFieldName("");
    setFieldType("String");
    setFieldValue("");
  };

  const handleSave = () => {
    const trimmedName = fieldName.trim();
    const trimmedValue = fieldValue.trim();
    if (trimmedName) {
      onAddField(id, trimmedName, fieldType, trimmedValue);
      setIsAdding(false);
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setFieldName("");
    setFieldValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleStartRenameTitle = (e) => {
    handleMenuClose(e);
    setIsEditingTitle(true);
    setEditTitle(title);
  };

  const handleSaveTitle = () => {
    const trimmed = editTitle.trim();
    if (trimmed) {
      onRenameContainer(id, trimmed);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      setIsEditingTitle(false);
    }
  };

  const handlePropChange = (key, newVal) => {
    onUpdateContainerData(id, {
      ...data,
      [key]: newVal
    });
  };

  const displayKeys = Object.keys(data || {}).filter((key) => isSchemaTab ? true : key !== "FieldName");
  const propertiesCount = displayKeys.length;

  return (
    <Card 
      elevation={1}
      className="schema-container-card"
      sx={{ 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
        }
      }}
      onClick={() => setIsPopupOpen(true)}
    >
      <CardHeader
        sx={{ 
          p: 2, 
          bgcolor: 'transparent',
          '& .MuiCardHeader-content': { overflow: 'hidden' }
        }}
        avatar={
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <IconButton size="small" component="div" sx={{ p: 0.5, mr: 1, pointerEvents: 'none' }}>
              {isSchemaTab ? <DataObject fontSize="small" color="primary" /> : <ViewList fontSize="small" />}
            </IconButton>
          </Box>
        }
        title={
          isEditingTitle ? (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                freeSolo
                options={[]}
                value={editTitle}
                onInputChange={(e, val) => setEditTitle(val || '')}
                sx={{ bgcolor: 'background.paper' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={titleInputRef}
                    size="small"
                    variant="outlined"
                    onKeyDown={handleTitleKeyDown}
                    inputProps={{ ...params.inputProps, maxLength: 45 }}
                  />
                )}
              />
              <IconButton onClick={handleSaveTitle} color="primary" size="small" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
                <Check fontSize="small" />
              </IconButton>
              <IconButton onClick={() => setIsEditingTitle(false)} color="error" size="small" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
                <X fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isSchemaTab ? 'primary.main' : 'text.primary' }}>
              {title}
            </Typography>
          )
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%', mt: 0.5 }}>
            <Chip 
              label={`${propertiesCount} ${propertiesCount === 1 ? 'Prop' : 'Props'}`} 
              size="small" 
              color={isSchemaTab ? "primary" : "default"}
              variant="outlined"
              sx={{ fontWeight: 'medium' }}
            />
            
            <IconButton 
              size="small" 
              onClick={handleMenuOpen}
              sx={{ color: 'text.secondary' }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleStartRenameTitle}>
                <Pencil fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                Rename {isSchemaTab ? 'Container' : 'Field'}
              </MenuItem>
            </Menu>
          </Box>
        }
      />

      <Dialog 
        open={isPopupOpen} 
        onClose={(e) => { e.stopPropagation(); setIsPopupOpen(false); }}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title} - Properties</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button size="small" onClick={handleToggleJsonView} sx={{ mr: 2 }} color={jsonError ? "error" : "primary"}>
              {isJsonView ? 'Visual View' : 'JSON View'}
            </Button>
            <IconButton onClick={() => setIsPopupOpen(false)}>
              <X />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, pb: isSchemaTab ? 2 : 1 }}>
          {isJsonView ? (
            <TextField
              multiline
              fullWidth
              minRows={10}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                try {
                  JSON.parse(e.target.value);
                  setJsonError("");
                } catch (err) {
                  setJsonError(err.message);
                }
              }}
              error={!!jsonError}
              helperText={jsonError || "Edit JSON above. Switching to Visual View will save your changes."}
              InputProps={{
                sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
              }}
            />
          ) : (
            <>
              <Box className="fields-list">
            {isSchemaTab ? (
              // 1. Schema Config Tab: render properties of the config object directly
              data ? (
                <JsonNode
                  label=""
                  value={data}
                  onChange={(updated) => onUpdateContainerData(id, updated)}
                  rootMode={true}
                />
              ) : (
                <Box sx={{ py: 3, textAlign: 'center', bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">No schema properties configured.</Typography>
                </Box>
              )
            ) : (
              // 2. Standard Tab: render each field property recursively
              <>
                {displayKeys.map((key) => (
                  <JsonNode
                    key={key}
                    label={key}
                    value={data[key]}
                    onChange={(newVal) => handlePropChange(key, newVal)}
                    onDelete={() => {
                      const newObj = { ...data };
                      delete newObj[key];
                      onUpdateContainerData(id, newObj);
                    }}
                  />
                ))}
              </>
            )}
          </Box>

          {/* Inline Add Sub-Property Form */}
          {isAdding && !isSchemaTab && (
            <Box className="inline-field-editor" sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Plus fontSize="small" /> Add New Sub-Property
              </Typography>
              <Box className="editor-row" sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box className="editor-input-group name-group" sx={{ flex: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={[]}
                    value={fieldName}
                    onInputChange={(e, val) => setFieldName(val || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Property Name"
                        inputRef={fieldInputRef}
                        fullWidth
                        size="small"
                        variant="outlined"
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. FieldType"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Box>

                {fieldType !== 'Object' && fieldType !== 'Array' && (
                  <Box className="editor-input-group value-group" sx={{ flex: 1 }}>
                    <Autocomplete
                      freeSolo
                      options={[]}
                      value={fieldValue}
                      onInputChange={(e, val) => setFieldValue(val || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Value"
                          fullWidth
                          size="small"
                          variant="outlined"
                          onKeyDown={handleKeyDown}
                          placeholder="e.g. input"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>
                )}
                
                <Box className="editor-input-group type-group" sx={{ width: 130 }}>
                  <TextField
                    select
                    label="Type"
                    fullWidth
                    size="small"
                    value={fieldType}
                    onChange={(e) => setFieldType(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="String">String</MenuItem>
                    <MenuItem value="Number">Number</MenuItem>
                    <MenuItem value="Boolean">Boolean</MenuItem>
                    <MenuItem value="Array">Array</MenuItem>
                    <MenuItem value="Object">Object</MenuItem>
                  </TextField>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button onClick={handleCancel} color="inherit" size="small">
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary" size="small" startIcon={<Check />}>
                  Save Property
                </Button>
              </Box>
            </Box>
          )}

        {!isSchemaTab && !isAdding && (
          <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-start' }}>
            <Button
              onClick={handleStartAdd}
              size="small"
              startIcon={<Plus />}
              variant="text"
              color="primary"
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Add Sub-Property
            </Button>
          </CardActions>
        )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPopupOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ContainerCard;
